import React, {useCallback, useContext, useEffect, useState} from 'react';
import ENDPOINT from '../../../constants/endpoints';
import Button from '../../../GbsUI/UI/Button';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import Select from '../../../GbsUI/UI/Select';
import TextInput from '../../../GbsUI/UI/TextInput';
import useApi from '../../../hooks/useApi';
import { useTaxonomy } from '../../../hooks/useTaxonomy';
import SignupLayout from '../components/Layout';
import {SignupContext} from '../context';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const SignupStepOne = ({history}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const {state, setState} = useContext(SignupContext);
  const {data: referrals, isLoading: referralsLoading} = useTaxonomy('REFERRAL_FIRMS');
  const [data, setData] = useState({
    FirstName: '',
    LastName: '',
    EmailAddress: '',
    GuestPhoneNumber: '',
    Password: '',
    flow: state.flow,
    hasReferral: false,
    ref: state.ref,
  });
  const onBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const onSubmit = useCallback(e => {
    e.preventDefault();
    setErrors(null);
    setLoading(true);
    const postData = {
      ...data,
      ref: data.ref?.value || '',
    };

    void api.postRequest(
      ENDPOINT.ACTION.ACCOUNT.SIGNUP,
      postData,
      {
        handleError: response => {
          if (response.data?.fields) {
            setErrors(response.data);
          } else {

          }
          setLoading(false);
        },
        handleSuccess: () => {
          window.location.replace('/signup/step/2');
        },
      }
    );
  }, [api, data]);

  useEffect(() => {
    setData((current) => (
      {
        ...current,
        flow: state.flow,
      }
    ));
  }, [state.flow]);

  useEffect(() => {
    if (state.ref && referrals.length) {
      const ref = referrals.find((item) => item.value === state.ref);

      if (ref) {
        setData((current) => ({
          ...current,
          hasReferral: true,
          ref,
        }));
      } else {
        setState((current) => ({
          ...current,
          ref: '',
        }));
      }
    }
  }, [state.ref, referrals, setState])

  return (
    <form onSubmit={onSubmit} autoComplete={'off'}>
      <SignupLayout
        title={'Step 1: Create Your Account'}
        footer={
          <>
            <Button
              type={'button'}
              size={'xl'}
              variant={'secondary'}
              icon={ArrowLeftIcon}
              iconProps={{ size: 21 }}
              iconPosition={'left'}
              onClick={onBack}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{ size: 21 }}
              iconPosition={'right'}
              disabled={loading}
            >
              Create Account and Continue
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item'}>
            <TextInput
              label={'First Name'}
              placeholder={'e.g. John'}
              help={'Enter the name you commonly go by'}
              value={data.FirstName}
              error={errors?.fields.FirstName}
              onChange={e => {
                setData({
                  ...data,
                  FirstName: e.target.value,
                });
              }}
              required
            />
          </div>
          <div className={'signup-form-item'}>
            <TextInput
              label={'Last Name'}
              placeholder={'e.g. Smith'}
              value={data.LastName}
              error={errors?.fields.LastName}
              onChange={e => {
                setData({
                  ...data,
                  LastName: e.target.value,
                });
              }}
              required
            />
          </div>
          <div className={'signup-form-item'}>
            <TextInput
              label={'Personal Email'}
              type={'email'}
              autoComplete={'off'}
              help={
                'Enter a personal email where you can securely receive recruiting info (avoid work/school emails which may change).'
              }
              placeholder={'yourname@example.com'}
              value={data.EmailAddress}
              error={errors?.fields.EmailAddress}
              onChange={e => {
                setData({
                  ...data,
                  EmailAddress: e.target.value,
                });
              }}
              required
            />
          </div>
          <div className={'signup-form-item'}>
            <PhoneInput
              value={data.GuestPhoneNumber}
              placeholder="Enter phone number"
              defaultCountry={'US'}
              label={'Phone number'}
              help={
                'Used only to contact you about potential recruiting opportunities.'
              }
              onChange={(v) => {
                setData({
                  ...data,
                  GuestPhoneNumber: v,
                })
              }}
              error={errors?.fields.GuestPhoneNumber}
              inputComponent={TextInput}
              required
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <TextInput
              label={'Password'}
              type={'password'}
              autoComplete={'new-password'}
              help={
                'Your password must contain a minimum of 6 characters. Avoid using predictable patterns like "123456" or "password" to enhance protection.'
              }
              value={data.Password}
              error={errors?.fields.Password}
              onChange={e => {
                setData({
                  ...data,
                  Password: e.target.value,
                });
              }}
              required
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <Checkbox
              onChange={e => {
                const checked = e.target.checked;
                setData({
                  ...data,
                  hasReferral: checked,
                  ref: '',
                });
                setState(current => ({
                  ...current,
                  hasReferral: checked,
                  ref: '',
                }));
              }}
              label={'Referred by a Partner Organization (Optional)'}
              checked={data.hasReferral}
            />
          </div>
          {data.hasReferral && (
            <div className={'signup-form-item signup-form-item--full'}>
              <Select
                label={
                  'Select your organization if referred by a GoBuyside Partner Organization:'
                }
                options={referrals}
                isLoading={referralsLoading}
                errors={errors?.fields.ref}
                value={data.ref}
                isRequired
                onChange={v => {
                  setData({
                    ...data,
                    ref: v,
                  });
                  setState(current => ({
                    ...current,
                    ref: v.value,
                  }));
                }}
              />
            </div>
          )}
        </div>
      </SignupLayout>
    </form>
  );
}

export default SignupStepOne;
